import React, { useLayoutEffect, useRef, useState } from "react";
import { Avatar, Box, Link, Skeleton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import Label from "src/components/Label";
import { s3baseUrl } from "src/config/config";
import { dueStatus, formatDueDate, memberName } from "./clientReviewUtils";

// Small building blocks of the Client Review Due card and the Client reviews page.

/** Avatar, name and email. */
export function ReviewMember({ row }) {
  const name = memberName(row);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Avatar
        alt={name}
        src={row.profile_image ? s3baseUrl + row.profile_image : undefined}
        sx={{ width: 32, height: 32 }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {row.email}
        </Typography>
      </Box>
    </Box>
  );
}

/** Due date with its status under it ("Overdue · 5 days", "Today", "In 3 days"). */
export function DueStatus({ row }) {
  const { color, label } = dueStatus(row);
  return (
    <>
      <Typography variant="body2">{formatDueDate(row.review_date)}</Typography>
      {/* Smaller than the default Label (22px / 12px). */}
      <Label
        variant="ghost"
        color={color}
        sx={{ height: 18, px: 0.75, fontSize: 11, fontWeight: 600, borderRadius: 1, mt: 0.25 }}
      >
        {label}
      </Label>
    </>
  );
}

// Opens the notepad without also opening the row (member profile).
const noteLinkProps = (onOpen) => ({
  component: "button",
  type: "button",
  variant: "caption",
  underline: "hover",
  onClick: (e) => {
    e.stopPropagation();
    onOpen();
  },
  onKeyDown: (e) => e.stopPropagation(),
  sx: { fontWeight: 600, verticalAlign: "baseline" },
});

/**
 * Notepad note in the row: up to two lines; clicking the note (or "Read more" when it is longer)
 * opens the notepad with the full note. No note → a dash.
 */
export function NotePreview({ text, onOpen }) {
  const textRef = useRef(null);
  const [cut, setCut] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    setCut(Boolean(el) && el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  if (!text) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 180, maxWidth: 300 }}>
      <Typography
        ref={textRef}
        variant="body2"
        color="text.secondary"
        role="button"
        tabIndex={0}
        title="Open notes"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        sx={{
          cursor: "pointer",
          "&:hover": { color: "text.primary" },
          // td is nowrap portal-wide (style.css); the note wraps to two lines.
          whiteSpace: "normal",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
      {cut && <Link {...noteLinkProps(onOpen)}>Read more</Link>}
    </Box>
  );
}

/** Tab label with a count pill; tone colours the pill (e.g. error for Overdue). */
export function CountTabLabel({ label, count, tone }) {
  const theme = useTheme();
  const main = tone ? theme.palette[tone].main : theme.palette.text.secondary;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      {label}
      {count !== undefined && (
        <Box
          component="span"
          sx={{
            minWidth: 22,
            px: 0.75,
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: "20px",
            textAlign: "center",
            color: tone && count > 0 ? theme.palette[tone].dark : theme.palette.text.secondary,
            bgcolor: alpha(main, tone && count > 0 ? 0.16 : 0.1),
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

/** Empty list: icon, short title and one line. */
export function ReviewsEmpty({ title, text }) {
  return (
    <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
      <EventAvailableOutlinedIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
      <Typography variant="subtitle2">{title}</Typography>
      {text && (
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      )}
    </Box>
  );
}

/** Loading rows in the shape of the table. */
export function ReviewsSkeleton({ rows = 4 }) {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.25 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="40%" />
            <Skeleton width="25%" />
          </Box>
          <Skeleton variant="rounded" width={96} height={22} />
        </Box>
      ))}
    </Box>
  );
}
